import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { shallowEqual } from 'react-redux';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { RootState } from '@store/store';
import { setUpdateLastPlay, setIsLoading } from '@/slices/chatChapterSlice';
import { useMutation, useQuery } from '@apollo/client';
import { START_CHAPTER, START_STORY, SAVE_USER_HISTORY } from '@api/story';
import { GET_USER_ONBOARDING_STORY } from '@api/user';
import { isPlayableChapter, onWriteCurrentPlay } from '@service/ChapterService';
import {
  LastPlay,
  MyCharacterInfo,
  OtherCharacterInfo,
  PlayStory,
  StartChapterResult,
} from '@customTypes/chapter';
import { UserOnboardingStory } from '@customTypes/user';
import { ChatPlayableType } from '@/customTypes/chatStory';
import {
  startStoryName,
  startChapterFirst,
  startChapter,
  viewAppConversionModal,
} from '@lib/eventManager';
import { isDevelopment } from '@common/utils';
import { DEEP_LINK, SERVER_ERROR_CODE, STORAGE_KEY } from '@common/values';
import { useChatPlayContext } from '@provider/ChatPlayProvider';
import OnboardingPopupProvider from '@provider/OnboardingPopupProvider';
import ChatError from '@components/commons/error/ChatError';
import ChatHeader from '@/components/chatStory/header/ChatHeader';
import OnBoardingPopup from '@/components/storyCommon/popup/onBoarding/OnBoardingPopup';
import AppInductionPopup from '@components/commons/popup/AppInductionPopup';
import SPLoading from '@components/commons/loading/SPLoading';

import DevTools from './DevTools';
import ChatList from './ChatList';
import { useInteractiveChapterQuery } from '@/operations/queries/chapter/__generated__/useInteractiveChapter.generated';
import { Story_Play_Type } from '@/baseType';
import { UserHistoryListDocument } from '@/operations/queries/home/__generated__/useUserHistoryList.generated';
import { setToastMessage } from '@/slices/alertSlice';
import useTranslation from 'next-translate/useTranslation';

const isDev = isDevelopment();

interface IChatStory {
  startChapterTransaction: any;
}

const ChatStory = ({ startChapterTransaction }: IChatStory) => {
  const route = useRouter();
  const { t } = useTranslation();
  const { chapterId } = route.query;
  const dispatchRTK = useAppDispatch();
  const { initChatData } = useChatPlayContext();
  const [isOnboardingPopup, setIsOnboardingPopup] = useState<boolean>();
  const [chatPlayableType, setChatPlayableType] =
    useState<ChatPlayableType>('play');
  const [isInitChat, setIsInitChat] = useState<boolean>(false);
  const [isShowAppPopup, setIsShowAppPopup] = useState<boolean>(false);
  const startChapterSpanRef = useRef<any>();
  const startStorySpanRef = useRef<any>();
  const onBoardingSpanRef = useRef<any>();

  const { isLoading } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const { isRestarting } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const { willUseCoin } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );

  useEffect(() => {
    initChapterData();

    return () => {
      sessionStorage.setItem(STORAGE_KEY.WATCHED_AD_CHAPTER_ID, '');
      sessionStorage.removeItem(STORAGE_KEY.COMMENT.COMMENT_TO_EDIT_INFO);
    };
  }, []);

  useEffect(() => {
    route.replace(
      {
        pathname: route.pathname,
        query: route.query,
      },
      undefined,
      { shallow: true },
    );
  }, [route.pathname]);

  useEffect(() => {
    if (isInitChat) {
      setIsInitChat(false);
      startChapterTransaction?.finish();
      initChatData();
    }
  }, [isInitChat]);

  const { data: userOnboardingStory } = useQuery<UserOnboardingStory>(
    GET_USER_ONBOARDING_STORY,
    {
      fetchPolicy: 'network-only',
    },
  );

  const { data: chapterData } = useInteractiveChapterQuery({
    variables: {
      chapterId: Number(chapterId),
    },
    skip: !!isNaN(Number(chapterId)),
    fetchPolicy: 'no-cache',
  });

  const [startStoryMutation, { loading: storyLoading }] = useMutation(
    START_STORY,
    {
      onCompleted: (res) => {
        startStorySpanRef.current?.finish();

        const lastPlay: LastPlay = res?.startStory;
        res && dispatchRTK(setUpdateLastPlay(lastPlay));
        setIsOnboardingPopup(false);
        setIsInitChat(true);

        const isOnboardingStory =
          !userOnboardingStory?.getUserMetric?.onBoardingStory;

        startStoryName({ name: chapterData?.getChapter.story.name });

        chapterData &&
          startChapter({
            storyName: chapterData.getChapter.story.name,
            storyId: chapterData.getChapter.storyId,
            chapterIndex: chapterData.getChapter.chapterIndex,
            chapterId: chapterData.getChapter.chapterId,
            start: isRestarting ? 'time_leap' : 'start',
            waitFree: Boolean(
              chapterData.getChapter.freeDependencyChapterIndex,
            ),
            adImpression:
              sessionStorage.getItem(STORAGE_KEY.WATCHED_AD_CHAPTER_ID) ===
              String(playChapterInfo?.playChapter.chapterId),
            price: chapterData.getChapter.price,
            pay: willUseCoin ? true : playChapterInfo?.playChapter.price === 0,
            contentType:
              chapterData.getChapter.story.playType ===
              Story_Play_Type.Interactive
                ? 'interactive'
                : 'novel',
            storyType: chapterData.getChapter.story.isMWS
              ? 'challenge'
              : 'home',
            withCustomCharacter:
              lastPlay.currentPlayingChapter.withAICustomCharacter === true,
          });

        // 온보딩 스토리(유저의 생애 최초 스토리)이고 타임리프가 아닌 경우 startChapterFirst
        isOnboardingStory &&
          !isRestarting &&
          chapterData &&
          startChapterFirst({
            storyName: chapterData?.getChapter.story.name,
            storyId: chapterData?.getChapter.storyId,
            chapterId: chapterData?.getChapter.chapterId,
            chapterIndex: chapterData?.getChapter.chapterIndex,
            contentType:
              chapterData?.getChapter.story.playType ===
              Story_Play_Type.Interactive
                ? 'interactive'
                : 'novel',
            storyType: chapterData?.getChapter.story.isMWS
              ? 'challenge'
              : 'home',
          });
      },
      // 읽고있는 스토리 업데이트
      refetchQueries: [
        {
          query: UserHistoryListDocument,
        },
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        if (
          error.message.includes(SERVER_ERROR_CODE.ADULT_VERIFICATION_REQUIRED)
        ) {
          dispatchRTK(
            setToastMessage(t('payment_toast_all_chapter_19_chapter_included')),
          );
          const redirectInfo = JSON.stringify({
            redirectUrl: `/story/${chapterData?.getChapter.storyId}`,
            createdAt: new Date(),
          });
          localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

          route.replace({
            pathname: `/adult`,
            query: {
              url: `/story/${chapterData?.getChapter.storyId}`,
              storyId: chapterData?.getChapter.storyId,
            },
          });
          return;
        }

        setChatPlayableType('info');
      },
    },
  );

  const [startChapterMutation, { loading: chapterLoading }] = useMutation(
    START_CHAPTER,
    {
      onCompleted: async (res) => {
        startChapterSpanRef.current?.finish();
        const result: StartChapterResult = res?.startChapter;
        res && dispatchRTK(setUpdateLastPlay(result.chapter.story.lastPlay));
        setIsInitChat(true);
        chapterData &&
          startChapter({
            storyName: chapterData.getChapter.story.name,
            storyId: chapterData.getChapter.storyId,
            chapterIndex: chapterData.getChapter.chapterIndex,
            chapterId: chapterData.getChapter.chapterId,
            start: isRestarting ? 'time_leap' : 'start',
            waitFree: Boolean(
              chapterData.getChapter.freeDependencyChapterIndex,
            ),
            adImpression:
              sessionStorage.getItem(STORAGE_KEY.WATCHED_AD_CHAPTER_ID) ===
              String(playChapterInfo?.playChapter.chapterId),
            price: chapterData.getChapter.price,
            pay: willUseCoin ? true : playChapterInfo?.playChapter.price === 0,
            contentType:
              chapterData.getChapter.story.playType ===
              Story_Play_Type.Interactive
                ? 'interactive'
                : 'novel',
            storyType: chapterData.getChapter.story.isMWS
              ? 'challenge'
              : 'home',
            withCustomCharacter:
              chapterData.getChapter.story.lastPlay?.currentPlayingChapter
                ?.withAICustomCharacter === true,
          });
      },
      // 읽고있는 스토리 업데이트
      refetchQueries: [
        {
          query: UserHistoryListDocument,
        },
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        if (
          error.message.includes(SERVER_ERROR_CODE.ADULT_VERIFICATION_REQUIRED)
        ) {
          dispatchRTK(
            setToastMessage(t('payment_toast_all_chapter_19_chapter_included')),
          );
          const redirectInfo = JSON.stringify({
            redirectUrl: `/story/${chapterData?.getChapter.storyId}`,
            createdAt: new Date(),
          });
          localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

          route.replace({
            pathname: `/adult`,
            query: {
              url: `/story/${chapterData?.getChapter.storyId}`,
              storyId: chapterData?.getChapter.storyId,
            },
          });
          return;
        }

        setChatPlayableType('info');
      },
    },
  );

  useEffect(() => {
    dispatchRTK(setIsLoading(storyLoading || chapterLoading));
  }, [storyLoading, chapterLoading]);

  const [saveUserHistory] = useMutation(SAVE_USER_HISTORY, {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    onCompleted: (propsResult) => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    onError: (error) => {},
    fetchPolicy: 'no-cache',
  });

  const initChapterData = () => {
    if (playChapterInfo) {
      const isPlayable = isPlayableChapter(playChapterInfo);

      if (isPlayable === 'play' || isRestarting) {
        setIsOnboardingPopup(
          playChapterInfo.playChapter.chapterIndex === 1 &&
            (playChapterInfo.onboardingPopups?.length ?? -1) > 0 &&
            (!playChapterInfo.currentPlayInfo || isRestarting),
        );

        if (
          playChapterInfo.playChapter.chapterIndex === 1 &&
          (!playChapterInfo.currentPlayInfo || isRestarting)
        ) {
          if ((playChapterInfo.onboardingPopups?.length ?? -1) > 0) {
            onBoardingSpanRef.current = startChapterTransaction?.startChild({
              op: 'StartOnboarding',
              description: '온보딩 팝업 시작',
            });
            setIsOnboardingPopup(true);
          } else {
            onFinishOnboarding();
          }
        } else {
          initStartChapter(playChapterInfo.playChapter.story);
        }

        saveUserHistory({
          variables: {
            storyId: playChapterInfo.playChapter.storyId,
          },
        });
      } else {
        setChatPlayableType(isPlayable);
      }
    }
  };

  /** 챕터 처음 시작 시 호출, 1챕터 처음 시작은 실행하지 않음 */
  const initStartChapter = (playStory: PlayStory) => {
    const chapterInfo = playChapterInfo?.playChapter;
    if (!chapterInfo) return;

    const currentPlay = playChapterInfo.currentPlayInfo;
    const thisChapter = playStory?.chapters.find(
      (item) => item.chapterId === chapterInfo.chapterId,
    );

    if (
      (thisChapter &&
        currentPlay &&
        thisChapter.chapterIndex !== 1 &&
        currentPlay.chapterId !== thisChapter.chapterId) ||
      isRestarting
    ) {
      onWriteCurrentPlay(chapterInfo.storyId, undefined);
      startChapterSpanRef.current = startChapterTransaction?.startChild({
        op: 'StartChapter',
        description: 'startChapter 요청',
      });
      startChapterMutation({
        variables: {
          storyId: chapterInfo.storyId,
          chapterId: chapterInfo.chapterId,
          willUseCoin: willUseCoin
            ? true
            : playChapterInfo?.playChapter.price === 0,
          isRestarting,
        },
      });
    } else {
      setIsInitChat(true);
      // 이어보기 시 호출하는 start_chapter
      startChapter({
        storyName: chapterInfo.story.name,
        storyId: chapterInfo.storyId,
        chapterIndex: chapterInfo.chapterIndex,
        chapterId: chapterInfo.chapterId,
        start: 'continue',
        waitFree: Boolean(chapterInfo.freeDependencyChapterIndex),
        adImpression:
          sessionStorage.getItem(STORAGE_KEY.WATCHED_AD_CHAPTER_ID) ===
          String(playChapterInfo?.playChapter.chapterId),
        price: chapterInfo.price,
        pay: willUseCoin ? true : playChapterInfo?.playChapter.price === 0,
        contentType: 'interactive',
        storyType: chapterInfo.story.isMWS ? 'challenge' : 'home',
        withCustomCharacter:
          chapterInfo.story.lastPlay?.currentPlayingChapter
            .withAICustomCharacter === true,
      });
    }
  };

  const onFinishOnboarding = (
    myCharacterInfo?: MyCharacterInfo,
    choiceProps?: Record<string, number | string>,
    otherCharacterInfoList?: OtherCharacterInfo[],
    playerClassId?: number,
  ) => {
    if (playChapterInfo) {
      const playChapter = playChapterInfo.playChapter;
      const info = {
        storyId: playChapter.storyId,
        isRestarting,
        firstName:
          myCharacterInfo?.firstName ||
          playChapterInfo?.mainCharacterName.firstName,
        lastName:
          myCharacterInfo?.lastName ||
          playChapterInfo?.mainCharacterName.lastName,
        willUseCoin: willUseCoin
          ? true
          : playChapterInfo?.playChapter.price === 0,
        profileImageId:
          myCharacterInfo?.profileImageId ??
          playChapter.story.mainCharacterImageFile?.fileId,
        popupProps: choiceProps ? choiceProps : {},
        customProfileFileInfo: myCharacterInfo?.customProfileFileInfo || null,
        otherCharacterInfoList: otherCharacterInfoList || null,
        playerClassId,
      };

      startStorySpanRef.current = startChapterTransaction?.startChild({
        op: 'StartStory',
        description: 'startStory 요청',
      });
      onBoardingSpanRef.current?.finish();

      isRestarting && onWriteCurrentPlay(info.storyId, undefined);
      startStoryMutation({
        variables: info,
      });
    }
  };

  const onClose = () => {
    route.push(`/story/${playChapterInfo?.playChapter.storyId}`);
  };

  return (
    <div css={container}>
      {chatPlayableType !== 'play' ? (
        <ChatError
          chatPlayableType={chatPlayableType}
          storyId={playChapterInfo?.playChapter?.storyId}
        />
      ) : (
        playChapterInfo && (
          <>
            <div className="story">
              <ChatHeader
                title={playChapterInfo.playChapter.name || ''}
                onPopUp={() => {
                  viewAppConversionModal({
                    where: 'chat_comment',
                    story: playChapterInfo.playChapter.name,
                    storyId: String(playChapterInfo.playChapter.storyId),
                  });

                  setIsShowAppPopup(true);
                }}
              />
              <section className="chat">
                {isOnboardingPopup === false && playChapterInfo.chatBlocks && (
                  <ChatList />
                )}
              </section>
            </div>
            {!isLoading && isOnboardingPopup && (
              <OnboardingPopupProvider onFinish={onFinishOnboarding}>
                <div className="onboarding">
                  {playChapterInfo.playChapter &&
                    playChapterInfo.playChapter.story && (
                      <OnBoardingPopup onClose={onClose} />
                    )}
                </div>
              </OnboardingPopupProvider>
            )}
          </>
        )
      )}
      {playChapterInfo && (
        <AppInductionPopup
          isShow={isShowAppPopup}
          storyName={playChapterInfo.playChapter.name}
          storyId={playChapterInfo.playChapter.storyId}
          where={'chat_comment'}
          onClose={() => {
            setIsShowAppPopup(false);
          }}
          deepLink={DEEP_LINK.CHAT_COMMENT}
        />
      )}
      {isLoading && <SPLoading hasBackDrop={true} />}
      {isDev && <DevTools />}
    </div>
  );
};

const container = css`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: ${isDev ? 'row' : 'column'};
  justify-content: center;
  align-items: ${isDev ? 'flex-start' : 'center'};
  background-color: ${theme.colors.gray900};

  .onboarding {
    width: 100%;
    max-width: 50em;
    height: 100%;
    position: fixed;
    z-index: 20;
  }

  .story {
    width: 100%;
    max-width: 50em;
    height: 100%;
    background-color: ${theme.colors.gray100};
    overflow-y: hidden;
  }

  .chat {
    height: 100%;
    padding-bottom: 4em;
  }
`;

export default ChatStory;
